import React from "react";
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Image,
  VStack,
  Flex,
  Center,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import epcImg from "../assets/epcmgmg.jpg";

const MotionBox = motion(Box);

export default function ServicesEpcManagement() {
  const processSteps = [
    {
      title: "Engineering Oversight",
      description:
        "Review and validation of detailed engineering designs to ensure technical soundness, regulatory compliance, and alignment with project objectives.",
    },
    {
      title: "Procurement Advisory",
      description:
        "Support in vendor evaluation, equipment selection, contract negotiation, and supply chain optimization to ensure cost-effective and timely procurement of critical components.",
    },
    {
      title: "Construction Supervision",
      description:
        "Monitor construction activities to ensure adherence to design specifications, quality standards, and safety protocols, while minimizing delays and cost overruns.",
    },
    {
      title: "Interface Management",
      description:
        "Coordinate among multiple stakeholders—developers, EPC contractors, OEMs, and utility partners—to ensure clarity of roles and responsibilities, and avoid conflicts.",
    },
    {
      title: "Quality Assurance & Risk Mitigation",
      description:
        "Implement quality control measures and proactively identify risks related to design, supply, or execution, ensuring corrective actions are taken promptly.",
    },
    {
      title: "Project Monitoring & Reporting",
      description:
        "Provide transparent and data-driven updates on progress, cost, and timelines, enabling informed decision-making for project stakeholders.",
    },
  ];

  return (
    <Box>
      <Box>
        {/* Hero Section with Background */}
        <Box
          position="relative"
          shadow="xl"
          mb={10}
          textAlign="center"
          overflow="hidden"
        >
          {/* Background Image */}
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            bgImage={`url(${epcImg})`}
            bgSize="cover"
            bgPosition="center"
            filter="brightness(0.6) blur(2px)"
            zIndex={0}
          />

          {/* Foreground Content */}
          <Box
            position="relative"
            zIndex={1}
            color="white"
            p={{ base: 10, md: 16 }}
          >
            <Heading
              fontSize={{ base: "3xl", md: "5xl" }}
              mb={4}
              textShadow="2px 2px 4px rgba(0,0,0,0.7)"
            >
              EPC Management (EPCM)
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              mt={4}
              maxW="3xl"
              mx="auto"
              textShadow="1px 1px 3px rgba(0,0,0,0.6)"
              fontWeight="bold"
            >
              Seamless execution of renewable energy projects—from concept to
              commissioning—through strategic oversight and technical expertise.
            </Text>

            <Text
              mt={4}
              maxW="3xl"
              mx="auto"
              textShadow="1px 1px 3px rgba(0,0,0,0.6)"
              fontWeight="bold"
            >
              We act as an independent interface between project developers,
              investors, and EPC contractors to safeguard quality, control
              costs, and ensure timely delivery. From concept to commissioning,
              our EPCM services ensure every stage of the project is optimized
              for performance and sustainability.
            </Text>
          </Box>
        </Box>

        {/* EPCM Process Section */}
        <Box
          bg="#F5FAF6"
          rounded="2xl"
          p={{ base: 6, md: 12 }}
          mb={10}
          mx={{ base: 4, md: 20 }}
          textAlign="center"
        >
          <Heading
            size="xl"
            mb={10}
            fontSize={{ base: "2xl", md: "5xl" }}
            color="#305D24"
          >
            Our Process
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
            {processSteps.map((step, i) => (
              <MotionBox
                key={i}
                p={6}
                bg="white"
                rounded="xl"
                shadow="md"
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
              >
                <Heading size="md" mb={3} color="#519C36">
                  {step.title}
                </Heading>
                <Text fontSize="sm" color="gray.700">
                  {step.description}
                </Text>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Box>

        {/* Call to Action */}
        <Center mt={16} mb={20}>
          <Box
            as="a"
            href="/contact"
            bg="#F6A11A"
            px={12}
            py={5}
            fontWeight="bold"
            borderRadius="3xl"
            shadow="xl"
            transition="0.3s"
            _hover={{
              bg: "#FFD580",
              color: "#305D24",
              transform: "scale(1.08)",
            }}
          >
            Start a Project
          </Box>
        </Center>
      </Box>
    </Box>
  );
}
